import { SiteSummaryModel } from '@shared/models';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SiteListItem } from '../SiteListItem';

export const ITEMS_PER_PAGE = 20;
const STEP_TO_NEXT_PAGE = 0.8;
const RENDER_AHEAD = 3;

const useScrollAware = () => {
  const [scrollTop, setScrollTop] = useState(0);
  const ref = useRef<HTMLElement | null>(null);

  const onScroll = (e: any) =>
    requestAnimationFrame(() => {
      setScrollTop(e.target.scrollTop);
    });

  useEffect(() => {
    const scrollContainer: HTMLElement | null = ref.current;

    if (scrollContainer) {
      setScrollTop(scrollContainer.scrollTop);
      scrollContainer.addEventListener('scroll', onScroll);
      return () => scrollContainer.removeEventListener('scroll', onScroll);
    }
  }, []);

  return [scrollTop, ref];
};

interface InfiniteScrollListProps {
  data: SiteSummaryModel[];
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  listHeight: number;
  totalItems: number;
  childHeight: number;
}

export const InfiniteScrollList: React.FC<InfiniteScrollListProps> = ({
  data,
  currentPage,
  setCurrentPage,
  listHeight,
  totalItems,
  childHeight,
}) => {
  const [scrollTop, ref] = useScrollAware();

  const totalHeightOfList: number = useMemo(
    () => Math.max(ITEMS_PER_PAGE * childHeight * (currentPage - 1), listHeight),
    [childHeight, listHeight, currentPage]
  );

  const startElementIndex = Math.max(0, Math.floor((scrollTop as number) / childHeight) - RENDER_AHEAD); // 1st element visible for user

  const visibleItemsAmount = useMemo(
    () => Math.min(totalItems - startElementIndex, Math.ceil(listHeight / childHeight) + 2 * RENDER_AHEAD),
    [totalItems, childHeight, listHeight, startElementIndex]
  );

  const offsetY = useMemo(() => startElementIndex * childHeight, [childHeight, startElementIndex]);

  const visibleElements = useMemo(() => {
    const newArLength = visibleItemsAmount || 0;
    const ar = newArLength > 0 ? new Array(newArLength).fill(null) : [];
    return ar.map((_, index) => {
      const siteData = data[index + startElementIndex];
      return <SiteListItem key={index} siteData={siteData} height={childHeight} />;
    });
  }, [data, childHeight, startElementIndex, visibleItemsAmount]);

  useEffect(() => {
    const currentItemsAmount = STEP_TO_NEXT_PAGE * ITEMS_PER_PAGE + ITEMS_PER_PAGE * (currentPage - 1);

    if (startElementIndex + visibleItemsAmount > currentItemsAmount && currentPage * ITEMS_PER_PAGE <= totalItems) {
      setCurrentPage((curr) => curr + 1);
    }
  }, [currentPage, totalItems, startElementIndex, visibleItemsAmount, setCurrentPage]);

  return (
    <div
      className="h-auto overflow-y-auto overflow-x-hidden max-h-full pr-0.5"
      ref={ref as React.MutableRefObject<any>}
    >
      <div style={{ width: '100%', height: totalHeightOfList }}>
        <div
          style={{
            willChange: 'transform',
            transform: `translateY(${offsetY}px)`,
          }}
        >
          {visibleElements}
        </div>
      </div>
    </div>
  );
};
